import { format } from '@if/core/date'
import AddIcon from '@mui/icons-material/Add'
import DownloadIcon from '@mui/icons-material/Download'
import { Box, Button, Chip, IconButton, Tooltip, Typography } from '@mui/material'
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid'
import FullScreenContainer from '@src/components/FullScreenContainer'
import {
  ETreeSimulationStatus,
  TreeSimulationCommandDto,
  useDownloadSimulationResult,
  useTreeSimulations,
} from '@src/data/api/tree-simulation/tree-simulation-api'
import { reportErrorToConsole } from '@src/services/error-logger'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'

const DEFAULT_PAGE_SIZE = 25

const statusColorMap: Record<ETreeSimulationStatus, 'default' | 'info' | 'success' | 'error'> = {
  [ETreeSimulationStatus.Pending]: 'default',
  [ETreeSimulationStatus.Processing]: 'info',
  [ETreeSimulationStatus.Completed]: 'success',
  [ETreeSimulationStatus.Failed]: 'error',
}

const TreeSimulationsListPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  const pageIndex = parseInt(searchParams.get('pageIndex') ?? '0', 10)
  const pageSize = parseInt(searchParams.get('pageSize') ?? String(DEFAULT_PAGE_SIZE), 10)

  const [simulations, isLoading] = useTreeSimulations(pageIndex, pageSize)
  // Drive next/prev without a total count: if a full page came back, assume there is more.
  const rowCount = pageIndex * pageSize + simulations.length + (simulations.length === pageSize ? 1 : 0)
  const [downloadResult] = useDownloadSimulationResult()

  const handlePaginationModelChange = useCallback(
    (model: GridPaginationModel) => {
      setSearchParams({ pageIndex: String(model.page), pageSize: String(model.pageSize) })
    },
    [setSearchParams],
  )

  const handleDownload = useCallback(
    async (id: string, event: React.MouseEvent) => {
      event.stopPropagation()
      try {
        const sasUrl = await downloadResult(id)
        window.open(sasUrl, '_blank')
      } catch (error) {
        reportErrorToConsole(error)
      }
    },
    [downloadResult],
  )

  const columns = useMemo<GridColDef<TreeSimulationCommandDto>[]>(
    () => [
      {
        field: 'name',
        headerName: t('treeSimulation.columns.name'),
        flex: 1,
        sortable: false,
      },
      {
        field: 'financingProgramId',
        headerName: t('treeSimulation.columns.financingProgram'),
        minWidth: 150,
        sortable: false,
        renderCell: (params) =>
          t(`EFinancingProgram.${params.row.financingProgramId}`, {
            ns: 'enum',
            defaultValue: params.row.financingProgramId,
          }),
      },
      {
        field: 'startDate',
        headerName: t('treeSimulation.columns.startDate'),
        minWidth: 120,
        sortable: false,
        renderCell: (params) => (params.row.startDate ? format(new Date(params.row.startDate), 'yyyy-MM-dd') : ''),
      },
      {
        field: 'endDate',
        headerName: t('treeSimulation.columns.endDate'),
        minWidth: 120,
        sortable: false,
        renderCell: (params) => (params.row.endDate ? format(new Date(params.row.endDate), 'yyyy-MM-dd') : ''),
      },
      {
        field: 'status',
        headerName: t('treeSimulation.columns.status'),
        minWidth: 130,
        sortable: false,
        renderCell: (params) => (
          <Chip
            label={t(`treeSimulation.status.${params.row.status}`, { defaultValue: params.row.status })}
            color={statusColorMap[params.row.status] ?? 'default'}
            size="small"
          />
        ),
      },
      {
        field: 'progress',
        headerName: t('treeSimulation.columns.progress'),
        minWidth: 120,
        sortable: false,
        renderCell: (params) => `${params.row.processedCount}/${params.row.totalCount}`,
      },
      {
        field: 'createdByUserFullname',
        headerName: t('treeSimulation.columns.createdBy'),
        minWidth: 150,
        sortable: false,
      },
      {
        field: 'createdOn',
        headerName: t('treeSimulation.columns.createdOn'),
        minWidth: 150,
        sortable: false,
        renderCell: (params) => (params.row.createdOn ? format(new Date(params.row.createdOn), 'yyyy-MM-dd HH:mm') : ''),
      },
      {
        field: 'actions',
        headerName: '',
        width: 60,
        sortable: false,
        renderCell: (params) => (
          <Tooltip title={t('treeSimulation.downloadResults')}>
            <span>
              <IconButton
                size="small"
                disabled={params.row.status !== ETreeSimulationStatus.Completed}
                onClick={(e) => void handleDownload(params.row.id, e)}
              >
                <DownloadIcon />
              </IconButton>
            </span>
          </Tooltip>
        ),
      },
    ],
    [t, handleDownload],
  )

  return (
    <FullScreenContainer>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1">
          {t('treeSimulation.listTitle')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/financial-engineer/tree-simulations/new')}
        >
          {t('treeSimulation.newSimulation')}
        </Button>
      </Box>

      <Box sx={{ height: '45rem', width: '100%' }}>
        <DataGrid
          disableColumnFilter
          rows={simulations}
          columns={columns}
          getRowId={(row: TreeSimulationCommandDto) => row.id}
          loading={isLoading}
          isRowSelectable={() => false}
          paginationMode="server"
          rowCount={rowCount}
          paginationModel={{ page: pageIndex, pageSize }}
          onPaginationModelChange={handlePaginationModelChange}
          pageSizeOptions={[25, 50, 100]}
        />
      </Box>
    </FullScreenContainer>
  )
}

export default TreeSimulationsListPage
